import React, { useEffect, useRef, useState } from "react";
import { api } from "./lib/api";
import { PAUSE_RADIO_EVENT } from "./RadioPlayer";
import "./styles/live.css";

// El backend revisa YouTube cada minuto; consultarlo más seguido no aporta
const POLL_MS = 60 * 1000;

let youTubeApiPromise = null;

const loadYouTubeApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);

  if (!youTubeApiPromise) {
    youTubeApiPromise = new Promise((resolve) => {
      const previousReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        previousReady?.();
        resolve(window.YT);
      };

      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      document.head.appendChild(script);
    });
  }

  return youTubeApiPromise;
};

export default function LivePlayer() {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const [status, setStatus] = useState(null); // { live, source, videoId, title }
  const [failed, setFailed] = useState(false);

  // Qué transmisión mostrar: la propia si están en vivo, si no la de noticias 24 horas
  useEffect(() => {
    let active = true;

    const fetchStatus = async () => {
      try {
        const res = await api.get("/live");
        if (active && res.data?.code === 1) {
          setStatus(res.data.data);
          setFailed(false);
        }
      } catch (error) {
        if (active) setFailed(true);
      }
    };

    fetchStatus();
    const intervalId = setInterval(fetchStatus, POLL_MS);

    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, []);

  const videoId = status?.videoId;

  useEffect(() => {
    if (!videoId) return undefined;
    let cancelled = false;

    loadYouTubeApi().then((YT) => {
      if (cancelled || !hostRef.current) return;

      const player = playerRef.current;
      if (player?.cueVideoById) {
        // Si la persona ya estaba viendo, sigue reproduciendo la nueva transmisión
        const playing = player.getPlayerState?.() === YT.PlayerState.PLAYING;
        if (playing) player.loadVideoById(videoId);
        else player.cueVideoById(videoId);
        return;
      }

      // YouTube reemplaza el nodo que recibe, por eso se crea uno fuera de React
      const node = document.createElement("div");
      hostRef.current.appendChild(node);
      playerRef.current = new YT.Player(node, {
        videoId,
        width: "100%",
        height: "100%",
        playerVars: { rel: 0, playsinline: 1 },
        events: {
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.PLAYING) {
              window.dispatchEvent(new Event(PAUSE_RADIO_EVENT));
            }
          },
        },
      });
    });

    return () => {
      cancelled = true;
    };
  }, [videoId]);

  useEffect(() => () => {
    playerRef.current?.destroy?.();
    playerRef.current = null;
  }, []);

  let caption = "";
  if (status?.live) caption = "Estás viendo La Mancha Amarilla en vivo.";
  else if (videoId) caption = "Ahora: CNN en Español, noticias las 24 horas. Nuestro noticiero: lunes a viernes, 6:00 p. m.";

  return (
    <div className="live-player">
      <div className="live-player__frame">
        <div ref={hostRef} className="live-player__video" />

        {!videoId && (
          <div className="live-player__placeholder">
            {failed || status ? "En este momento no hay transmisión disponible." : "Cargando transmisión..."}
          </div>
        )}
      </div>

      {caption && (
        <p className="live-player__caption">
          {status?.live && <span className="live-player__badge">EN VIVO</span>}
          {caption}
        </p>
      )}
    </div>
  );
}
